<script lang="ts">
  import type { Expense } from "../../lib/types";
  import { categoryIcons, entryIcons } from "../../lib/icons";
  import { formatCurrency } from "../../lib/utils";

  let {
    expenses,
    compact = false,
    clickable = true,
    showParticipantButton = true,
    onSelect,
    onOpenParticipants,
  }: {
    expenses: Expense[];
    compact?: boolean;
    clickable?: boolean;
    showParticipantButton?: boolean;
    onSelect?: (expense: Expense) => void;
    onOpenParticipants?: (expense: Expense) => void;
  } = $props();

  function handleClick(expense: Expense) {
    if (clickable) {
      onSelect?.(expense);
    }
  }

  function handleOpenParticipants(event: MouseEvent | KeyboardEvent, expense: Expense) {
    event.stopPropagation();
    onOpenParticipants?.(expense);
  }

  function handleParticipantsKeydown(event: KeyboardEvent, expense: Expense) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenParticipants(event, expense);
    }
  }

  const categoryLabels: Record<string, string> = {
    food: "식비",
    transport: "교통",
    accommodation: "숙박",
    shopping: "쇼핑",
    activity: "액티비티",
    other: "기타",
  };

  const entryMethodInfo: Record<string, { icon: string; label: string }> = {
    manual:   { icon: entryIcons.manual,   label: "수동 입력" },
    ocr:      { icon: entryIcons.scan,     label: "영수증 스캔" },
    imported: { icon: entryIcons.imported, label: "외부 가져오기" },
  };

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
    });
  }
</script>

<div class="space-y-3">
  {#each compact ? expenses.slice(0, 5) : expenses as expense (expense.id)}
    {@const entryMethod = entryMethodInfo[expense.entryMethod] || entryMethodInfo.manual}
    <button
      type="button"
      onclick={() => handleClick(expense)}
      class="w-full flex items-center justify-between p-4 rounded-lg transition-all text-left expense-list__card {clickable ? 'cursor-pointer' : ''}"
    >
      <div class="flex items-center">
        <div class="relative">
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center expense-list__icon-wrap"
          >
            <svg class="w-5 h-5 expense-list__accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {@html categoryIcons[expense.category] ?? categoryIcons.other}
            </svg>
          </div>
          {#if expense.entryMethod === "ocr" || expense.hasReceipt}
            <span
              class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center expense-list__receipt-badge"
              title={entryMethod.label}
            >
              <svg class="w-3 h-3 expense-list__receipt-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {@html entryIcons.scan}
              </svg>
            </span>
          {/if}
        </div>
        <div class="ml-3">
          <div class="flex items-center gap-2">
            <p class="font-medium expense-list__title">{expense.merchantName}</p>
            {#if expense.entryMethod === "ocr" && expense.modified}
              <span class="px-1.5 py-0.5 text-xs rounded expense-list__edited-badge">
                수정됨
              </span>
            {/if}
          </div>
          <p class="text-sm expense-list__meta">
            {categoryLabels[expense.category]} · {formatDate(expense.transactedAt)}
            {#if expense.hasReceipt}
              <span class="expense-list__receipt-text">· 영수증</span>
            {/if}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3">
        {#if showParticipantButton}
          <div
            role="button"
            tabindex="0"
            onclick={(e) => handleOpenParticipants(e, expense)}
            onkeydown={(e) => handleParticipantsKeydown(e, expense)}
            class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer expense-list__participants-badge"
            title="같이 지출한 사람 수정"
            aria-label="참여자 수정"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>{expense.participants?.length || 0}명</span>
          </div>
        {/if}

        <div class="text-right">
          <p class="font-semibold expense-list__title">
            {expense.originalCurrency === "KRW" ? "₩" : expense.originalCurrency}
            {formatCurrency(expense.krwAmountCents)}
          </p>
          <p class="text-sm expense-list__meta">{expense.payer?.name || "미지정"}</p>
        </div>
      </div>
    </button>
  {/each}
</div>

{#if expenses.length === 0}
  <div class="text-center py-8 expense-list__meta">
    지출 내역이 없습니다.
  </div>
{/if}

<style>
  .expense-list__card {
    background: var(--surface);
    border: 1px solid var(--border);
  }

  .expense-list__card:hover {
    border-color: color-mix(in srgb, var(--accent) 40%, var(--border));
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  }

  .expense-list__icon-wrap {
    background: color-mix(in srgb, var(--surface-hover) 92%, transparent);
  }

  .expense-list__accent {
    color: var(--accent);
  }

  .expense-list__receipt-badge {
    background: rgba(129, 140, 248, 0.18);
  }

  .expense-list__receipt-icon,
  .expense-list__receipt-text {
    color: #6366f1;
  }

  .expense-list__title {
    color: var(--text-primary);
  }

  .expense-list__meta {
    color: var(--text-secondary);
  }

  .expense-list__edited-badge {
    background: #fff4cc;
    color: #9a6700;
  }

  .expense-list__participants-badge {
    background: color-mix(in srgb, var(--surface-hover) 90%, transparent);
    color: var(--text-secondary);
    border: 1px solid var(--border);
  }

  .expense-list__participants-badge:hover {
    background: color-mix(in srgb, var(--accent-dim) 70%, var(--surface-hover));
    color: var(--accent);
    border-color: color-mix(in srgb, var(--accent) 40%, var(--border));
  }

  @media (prefers-color-scheme: dark) {
    .expense-list__card:hover {
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
    }

    .expense-list__edited-badge {
      background: rgba(245, 158, 11, 0.16);
      color: #fcd34d;
    }

    .expense-list__receipt-badge {
      background: rgba(129, 140, 248, 0.24);
    }
  }
</style>
